// The admin's dashboard: the periods it can show, how its numbers and time
// buckets read, and the colours its charts share.

export type DashboardRange = 'today' | '7d' | '30d' | '90d';

export interface DashboardRangeInfo {
  value: DashboardRange;
  /** On the filter row: "Today", "7 days". */
  label: string;
  /** In a sentence: "today", "in the last 7 days". */
  within: string;
  /** What it is compared with: "yesterday", "the 7 days before". */
  previous: string;
}

export const DASHBOARD_RANGES: DashboardRangeInfo[] = [
  // Today so far is compared with yesterday up to the same hour.
  { value: 'today', label: 'Today', within: 'today', previous: 'yesterday so far' },
  { value: '7d', label: '7 days', within: 'in the last 7 days', previous: 'the 7 days before' },
  { value: '30d', label: '30 days', within: 'in the last 30 days', previous: 'the 30 days before' },
  { value: '90d', label: '90 days', within: 'in the last 90 days', previous: 'the 90 days before' },
];

export const DEFAULT_DASHBOARD_RANGE: DashboardRange = '7d';

export function dashboardRange(value?: string | null): DashboardRangeInfo {
  return DASHBOARD_RANGES.find((range) => range.value === value) || DASHBOARD_RANGES[1];
}

/** The viewer's IANA time zone, for the server to bucket by; UTC when unknown. */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (error) {
    return 'UTC';
  }
}

/** "EAT", "GMT+3" — the zone as the filter row names it. */
export function timeZoneShort(zone: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part ? part.value : zone;
  } catch (error) {
    return zone;
  }
}

const trimZero = (text: string) => text.replace(/\.0$/, '');

/** 1,284 · 12.9K · 4.2M — a value at a glance. */
export function compactNumber(value: number | null | undefined): string {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs < 10000) return n.toLocaleString('en-US');
  if (abs < 1e6) return `${trimZero((n / 1e3).toFixed(1))}K`;
  return `${trimZero((n / 1e6).toFixed(1))}M`;
}

/** "+12", "−3", "0" — a signed count. */
export function signed(value: number): string {
  const n = Number(value) || 0;
  if (n > 0) return `+${n.toLocaleString('en-US')}`;
  if (n < 0) return `−${Math.abs(n).toLocaleString('en-US')}`;
  return '0';
}

/** "12%" of a whole; 0% of nothing. */
export function percentOf(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

export type DeltaTone = 'up' | 'down' | 'muted';

export interface Delta {
  text: string;
  tone: DeltaTone;
}

/**
 * How a count compares with the period before: the change, with the percent
 * when there is something to take it of, and whether that is good — up is,
 * unless `upIsBad` (failed sign-ins, tickets opened).
 */
export function compare(current: number, previous: number, previousLabel: string, upIsBad = false): Delta {
  const change = (Number(current) || 0) - (Number(previous) || 0);
  if (change === 0) return { text: `Same as ${previousLabel}`, tone: 'muted' };
  const percent = previous > 0 ? ` (${change > 0 ? '+' : '−'}${Math.round((Math.abs(change) / previous) * 100)}%)` : '';
  const good = change > 0 ? !upIsBad : upIsBad;
  return { text: `${signed(change)}${percent} vs ${previousLabel}`, tone: good ? 'up' : 'down' };
}

export type BucketKind = 'hour' | 'day';

const format = (date: Date, zone: string, options: Intl.DateTimeFormatOptions): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone }).format(date);
  } catch (error) {
    // A zone Intl doesn't know: the viewer's own clock is the next best thing.
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
};

/**
 * How a bucket is named: short for an axis ("Sep 30", "3 PM"), in full for a
 * tooltip or table ("Wed, Sep 30, 2026", "Wed, Sep 30, 3 PM – 4 PM"). Read in
 * the zone the server bucketed by, so the day is the viewer's day.
 */
export function bucketLabels(at: string, bucket: BucketKind, zone: string): { short: string; full: string } {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return { short: '', full: '' };
  if (bucket === 'hour') {
    const hour = format(date, zone, { hour: 'numeric' });
    const next = format(new Date(date.getTime() + 60 * 60 * 1000), zone, { hour: 'numeric' });
    const day = format(date, zone, { weekday: 'short', month: 'short', day: 'numeric' });
    return { short: hour, full: `${day}, ${hour} – ${next}` };
  }
  return {
    short: format(date, zone, { month: 'short', day: 'numeric' }),
    full: format(date, zone, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
  };
}

/**
 * The chart palette, validated (dataviz six checks) on the admin's white
 * surface: indigo, orange and aqua in this order pass every gate. Each app is
 * the same colour on every chart.
 */
export const CHART_COLORS = {
  screenkode: '#6366F1',
  chabad_digital: '#EB6834',
  admin: '#199E70',
  /** The one hue a single-series bar chart wears. */
  accent: '#6366F1',
  /** A sparkline's history, before its current period in the accent. */
  history: '#C9CED8',
};

/**
 * Where activity was done, as the charts list it — Screenkode's brand first.
 * Activity from none of these (no address recorded, local development, an
 * integration) is in the totals but drawn as no series.
 */
export const ACTIVITY_CHANNELS = [
  { key: 'screenkode', label: 'Screenkode', color: CHART_COLORS.screenkode },
  { key: 'chabad_digital', label: 'Chabad Digital', color: CHART_COLORS.chabad_digital },
  { key: 'admin', label: 'Screenkode Admin', color: CHART_COLORS.admin },
];
